import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ChartBarIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import AIFeatureAnalytics from '../../components/charts/AIFeatureAnalytics';

interface AIFeature {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  subscriptionTiers: string[];
  usageLimit?: number;
  createdAt: Date;
  updatedAt: Date;
}

interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  features: string[];
}

type TabType = 'features' | 'analytics';

const AIFeaturesAdmin: React.FC = () => {
  const [features, setFeatures] = useState<AIFeature[]>([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState<SubscriptionTier[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<AIFeature | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('features');
  const [newFeature, setNewFeature] = useState<Partial<AIFeature>>({
    name: '',
    description: '',
    status: 'active',
    subscriptionTiers: [],
  });

  useEffect(() => {
    // TODO: Fetch features and subscription tiers from API
    fetchFeatures();
    fetchSubscriptionTiers();
  }, []);

  const fetchFeatures = async () => {
    // TODO: Implement API call
    // For now, using mock data
    setFeatures([
      {
        id: '1',
        name: 'Smart Message Optimization',
        description: 'AI-powered message optimization for better engagement',
        status: 'active',
        subscriptionTiers: ['emerald', 'crystal', 'sapphire'],
        usageLimit: 1000,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // Add more mock features as needed
    ]);
  };

  const fetchSubscriptionTiers = async () => {
    // TODO: Implement API call
    // For now, using mock data with the specific packages
    setSubscriptionTiers([
      {
        id: 'emerald',
        name: 'Emerald',
        price: 9.99,
        features: ['basic_messaging'],
      },
      {
        id: 'crystal',
        name: 'Crystal',
        price: 19.99,
        features: ['basic_messaging', 'smart_optimization'],
      },
      {
        id: 'sapphire',
        name: 'Sapphire',
        price: 29.99,
        features: ['basic_messaging', 'smart_optimization', 'advanced_analytics'],
      },
      {
        id: 'diamond',
        name: 'Diamond',
        price: 49.99,
        features: ['basic_messaging', 'smart_optimization', 'advanced_analytics', 'priority_support'],
      },
      {
        id: 'silver',
        name: 'Silver',
        price: 39.99,
        features: ['basic_messaging', 'smart_optimization', 'basic_analytics'],
      },
      {
        id: 'gold',
        name: 'Gold',
        price: 59.99,
        features: ['basic_messaging', 'smart_optimization', 'advanced_analytics', 'priority_support', 'custom_features'],
      },
      {
        id: 'platinum',
        name: 'Platinum',
        price: 99.99,
        features: ['basic_messaging', 'smart_optimization', 'advanced_analytics', 'priority_support', 'custom_features', 'dedicated_support'],
      },
    ]);
  };

  const handleAddFeature = async () => {
    // TODO: Implement API call
    const feature: AIFeature = {
      id: Date.now().toString(),
      ...newFeature,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as AIFeature;

    setFeatures([...features, feature]);
    setIsAddModalOpen(false);
    setNewFeature({
      name: '',
      description: '',
      status: 'active',
      subscriptionTiers: [],
    });
  };

  const handleEditFeature = async (feature: AIFeature) => {
    // TODO: Implement API call
    setFeatures(features.map(f => f.id === feature.id ? feature : f));
    setIsEditModalOpen(false);
    setSelectedFeature(null);
  };

  const handleDeleteFeature = async (featureId: string) => {
    // TODO: Implement API call
    setFeatures(features.filter(f => f.id !== featureId));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'features':
        return (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-medium">AI Features List</h2>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New Feature
              </button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Tiers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage Limit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {features.map((feature) => (
                  <tr key={feature.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{feature.name}</div>
                      <div className="text-sm text-gray-500">{feature.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        feature.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {feature.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {feature.subscriptionTiers.join(', ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {feature.usageLimit || 'Unlimited'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedFeature(feature);
                          setIsEditModalOpen(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteFeature(feature.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'analytics':
        return <AIFeatureAnalytics featureId={selectedFeature?.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">AI Features Administration</h1>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('features')}
            className={`${
              activeTab === 'features'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ListBulletIcon className="w-5 h-5 mr-2" />
            Features List
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`${
              activeTab === 'analytics'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ChartBarIcon className="w-5 h-5 mr-2" />
            Analytics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Add Feature Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-medium mb-4">Add New AI Feature</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleAddFeature(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={newFeature.name}
                    onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newFeature.description}
                    onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subscription Packages</label>
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto border rounded-md p-2">
                    {subscriptionTiers.map(tier => (
                      <label key={tier.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={newFeature.subscriptionTiers?.includes(tier.id)}
                          onChange={(e) => {
                            const updatedTiers = e.target.checked
                              ? [...(newFeature.subscriptionTiers || []), tier.id]
                              : (newFeature.subscriptionTiers || []).filter(id => id !== tier.id);
                            setNewFeature({ ...newFeature, subscriptionTiers: updatedTiers });
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-900">{tier.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Usage Limit</label>
                  <input
                    type="number"
                    value={newFeature.usageLimit}
                    onChange={(e) => setNewFeature({ ...newFeature, usageLimit: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Add Feature
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Feature Modal */}
      {isEditModalOpen && selectedFeature && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-medium mb-4">Edit AI Feature</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleEditFeature(selectedFeature); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={selectedFeature.name}
                    onChange={(e) => setSelectedFeature({ ...selectedFeature, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={selectedFeature.description}
                    onChange={(e) => setSelectedFeature({ ...selectedFeature, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={selectedFeature.status}
                    onChange={(e) => setSelectedFeature({ ...selectedFeature, status: e.target.value as 'active' | 'inactive' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subscription Packages</label>
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto border rounded-md p-2">
                    {subscriptionTiers.map(tier => (
                      <label key={tier.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedFeature.subscriptionTiers.includes(tier.id)}
                          onChange={(e) => {
                            const updatedTiers = e.target.checked
                              ? [...selectedFeature.subscriptionTiers, tier.id]
                              : selectedFeature.subscriptionTiers.filter(id => id !== tier.id);
                            setSelectedFeature({ ...selectedFeature, subscriptionTiers: updatedTiers });
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-900">{tier.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Usage Limit</label>
                  <input
                    type="number"
                    value={selectedFeature.usageLimit}
                    onChange={(e) => setSelectedFeature({ ...selectedFeature, usageLimit: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedFeature(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIFeaturesAdmin; 